import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Alert,
    CircularProgress,
    InputAdornment,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import {
    Visibility,
    VisibilityOff,
    QrCodeScanner,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { giftcardService } from '../services/giftcardService';
import storeService from '../services/storeService';
import QRCodeScanner from '../components/QRCodeScanner';
import { useFeatureConfig } from '../hooks/useFeatureConfig';
import { getConfiguration } from '../services/coreService';

const CustomerForm = () => {
    const navigate = useNavigate();
    const { qrCodeSupported, loaded } = useFeatureConfig();
    const defaultConsentMessage = "I consent to receive email and SMS notification for discount and promotion information.";
    
    const [consentMessage, setConsentMessage] = useState(defaultConsentMessage);
    const [privacyAgreement, setPrivacyAgreement] = useState('');
    const [privacyAgreed, setPrivacyAgreed] = useState(false);
    const [privacyError, setPrivacyError] = useState(false);
    
    const [formData, setFormData] = useState({
        giftcardNumber: '',
        storeName: '',
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        pin: '',
        repeatPin: '',
        notificationConsent: true
    });

    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [showPin, setShowPin] = useState(false);
    const [showRepeatPin, setShowRepeatPin] = useState(false);
    const [errors, setErrors] = useState({});
    const [submitMessage, setSubmitMessage] = useState({ type: '', message: '' });
    const [qrScannerOpen, setQrScannerOpen] = useState(false);

    // Load stores and consent message on component mount
    useEffect(() => {
        const loadInitialData = async () => {
            try {
                setLoading(true);
                
                // Load stores
                const storeData = await storeService.getStores();
                setStores(storeData);
                
                // Load consent message from config (Application ID 1)
                try {
                    const config = await getConfiguration(1, 'ConsentMessage');
                    if (config && config.length > 0 && config[0].ConfigValue) {
                        setConsentMessage(config[0].ConfigValue);
                    }
                } catch (configError) {
                    console.log('Using default consent message');
                    // Keep default message if config fetch fails
                }
                
                // Load privacy agreement from config (Application ID 1)
                try {
                    const privacyConfig = await getConfiguration(1, 'PrivacyAgreement');
                    if (privacyConfig && privacyConfig.length > 0 && privacyConfig[0].ConfigValue) {
                        setPrivacyAgreement(privacyConfig[0].ConfigValue);
                    }
                } catch (configError) {
                    console.log('No privacy agreement configured');
                    // No privacy agreement configured, that's okay
                }
            } catch (error) {
                console.error('Error loading initial data:', error);
                setSubmitMessage({ type: 'error', message: 'Failed to load store list' });
            } finally {
                setLoading(false);
            }
        };

        loadInitialData();
    }, []);

    // Handle input changes
    const handleInputChange = (field) => (event) => {
        const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        // Giftcard Number validation
        if (!formData.giftcardNumber.trim()) {
            newErrors.giftcardNumber = 'Giftcard Number is required';
        }

        // Store Name validation
        if (!formData.storeName) {
            newErrors.storeName = 'Store Name is required';
        }

        // First Name validation
        if (!formData.firstName.trim()) {
            newErrors.firstName = 'First Name is required';
        }

        // Last Name validation
        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Last Name is required';
        }

        // Email format validation
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        // PIN validation
        if (!formData.pin) {
            newErrors.pin = 'PIN is required';
        } else if (!/^\d{4}$/.test(formData.pin)) {
            newErrors.pin = 'PIN must be exactly 4 digits';
        }

        // Repeat PIN validation
        if (!formData.repeatPin) {
            newErrors.repeatPin = 'Please confirm your PIN';
        } else if (formData.pin !== formData.repeatPin) {
            newErrors.repeatPin = 'PINs do not match';
        }

        // Email or Phone requirement
        if (!formData.email.trim() && !formData.phone.trim()) {
            newErrors.email = 'Either email or phone number is required';
            newErrors.phone = 'Either email or phone number is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async (event) => {
        event.preventDefault();

        // Check privacy agreement first (if privacy agreement exists)
        if (privacyAgreement && !privacyAgreed) {
            setPrivacyError(true);
            setSubmitMessage({ 
                type: 'error', 
                message: 'Please confirm you have read and agree the Privacy Agreement' 
            });
            return;
        }

        if (!validateForm()) {
            return;
        }

        setSubmitting(true);
        setSubmitMessage({ type: '', message: '' });
        setPrivacyError(false);

        try {
            // Clean phone number - remove all non-digit characters
            const cleanedPhone = formData.phone ? formData.phone.replace(/\D/g, '') : null;
            
            const response = await giftcardService.activateGiftcard({
                giftcardNumber: formData.giftcardNumber,
                storeId: formData.storeName,
                firstName: formData.firstName,
                lastName: formData.lastName,
                phone: cleanedPhone,
                email: formData.email || null,
                pin: formData.pin,
                notificationConsent: formData.notificationConsent
            });

            // Navigate to success page
            navigate('/activation-success');

        } catch (error) {
            console.error('Error activating giftcard:', error);
            const errorMessage = error.response?.data?.message || 'Failed to activate giftcard';
            setSubmitMessage({ type: 'error', message: errorMessage });
        } finally {
            setSubmitting(false);
        }
    };

    const togglePinVisibility = () => {
        setShowPin(!showPin);
    };

    const toggleRepeatPinVisibility = () => {
        setShowRepeatPin(!showRepeatPin);
    };

    const handleQrScanSuccess = (qrData) => {
        setFormData(prev => ({
            ...prev,
            giftcardNumber: qrData
        }));
        // Clear any existing error for giftcard number
        if (errors.giftcardNumber) {
            setErrors(prev => ({
                ...prev,
                giftcardNumber: ''
            }));
        }
    };

    const handleOpenQrScanner = () => {
        setQrScannerOpen(true);
    };

    const handleCloseQrScanner = () => {
        setQrScannerOpen(false);
    };

    return (
        <Box sx={{ 
            minHeight: '100vh', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            bgcolor: 'grey.50',
            p: 2
        }}>
            <Paper elevation={3} sx={{ 
                p: 4, 
                maxWidth: 600, 
                width: '100%',
                borderRadius: 2
            }}>
                <Typography variant="h4" component="h1" gutterBottom align="center" color="primary">
                    Customer Giftcard Activation
                </Typography>
                
                <Typography variant="body1" sx={{ mb: 4, textAlign: 'center', color: 'text.secondary' }}>
                    Please fill out the form below to activate your giftcard
                </Typography>

                {submitMessage.message && (
                    <Alert 
                        severity={submitMessage.type} 
                        sx={{ mb: 3 }}
                        onClose={() => setSubmitMessage({ type: '', message: '' })}
                    >
                        {submitMessage.message}
                    </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Row 1: Giftcard Number - Full Width */}
                    <TextField
                        fullWidth
                        label="Giftcard Number"
                        value={formData.giftcardNumber}
                        onChange={handleInputChange('giftcardNumber')}
                        error={!!errors.giftcardNumber}
                        helperText={errors.giftcardNumber}
                        required
                        InputProps={{
                            endAdornment: (loaded && qrCodeSupported) ? (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={handleOpenQrScanner}
                                        edge="end"
                                        color="primary"
                                        title="Scan QR Code"
                                    >
                                        <QrCodeScanner />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                    />

                    {/* Row 2: Store Name - Full Width */}
                    <FormControl fullWidth error={!!errors.storeName} required>
                        <InputLabel>Store Name</InputLabel>
                        <Select
                            value={formData.storeName}
                            onChange={handleInputChange('storeName')}
                            label="Store Name"
                            disabled={loading}
                        >
                            {stores.map((store) => (
                                <MenuItem key={store.StoreId} value={store.StoreId}>
                                    {store.StoreName}
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.storeName && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                                {errors.storeName}
                            </Typography>
                        )}
                    </FormControl>

                    {/* Row 3: First Name and Last Name - Side by Side */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            fullWidth
                            label="First Name"
                            value={formData.firstName}
                            onChange={handleInputChange('firstName')}
                            error={!!errors.firstName}
                            helperText={errors.firstName}
                            required
                        />
                        <TextField
                            fullWidth
                            label="Last Name"
                            value={formData.lastName}
                            onChange={handleInputChange('lastName')}
                            error={!!errors.lastName}
                            helperText={errors.lastName}
                            required
                        />
                    </Box>

                    {/* Row 4: Phone Number and Email - Side by Side */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            fullWidth
                            label="Phone Number"
                            value={formData.phone}
                            onChange={handleInputChange('phone')}
                            error={!!errors.phone}
                            helperText={errors.phone}
                        />
                        <TextField
                            fullWidth
                            label="Email"
                            type="email"
                            value={formData.email}
                            onChange={handleInputChange('email')}
                            error={!!errors.email}
                            helperText={errors.email}
                        />
                    </Box>

                    {/* Row 5: PIN and Repeat PIN - Side by Side */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            fullWidth
                            label="PIN (4 digits)"
                            type={showPin ? 'text' : 'password'}
                            value={formData.pin}
                            onChange={handleInputChange('pin')}
                            error={!!errors.pin}
                            helperText={errors.pin}
                            inputProps={{ maxLength: 4 }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={togglePinVisibility}
                                            edge="end"
                                        >
                                            {showPin ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            required
                        />
                        <TextField
                            fullWidth
                            label="Repeat PIN"
                            type={showRepeatPin ? 'text' : 'password'}
                            value={formData.repeatPin}
                            onChange={handleInputChange('repeatPin')}
                            error={!!errors.repeatPin}
                            helperText={errors.repeatPin}
                            inputProps={{ maxLength: 4 }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={toggleRepeatPinVisibility}
                                            edge="end"
                                        >
                                            {showRepeatPin ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            required
                        />
                    </Box>

                    {/* Privacy Agreement Section - Only show if privacy agreement exists */}
                    {privacyAgreement && (
                        <Box sx={{ mt: 3, mb: 0 }}>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                Privacy Agreement:
                            </Typography>
                            <Paper 
                                variant="outlined" 
                                sx={{ 
                                    p: 2, 
                                    maxHeight: '200px', 
                                    overflowY: 'auto',
                                    backgroundColor: '#f9f9f9',
                                    mb: 1
                                }}
                            >
                                <div dangerouslySetInnerHTML={{ __html: privacyAgreement }} />
                            </Paper>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={privacyAgreed}
                                        onChange={(e) => {
                                            setPrivacyAgreed(e.target.checked);
                                            if (e.target.checked) {
                                                setPrivacyError(false);
                                            }
                                        }}
                                        color="primary"
                                        sx={{
                                            '&.Mui-checked': {
                                                color: privacyError ? 'error.main' : 'primary.main',
                                            },
                                            color: privacyError ? 'error.main' : 'default',
                                        }}
                                    />
                                }
                                label="I confirm I have read and agree the Privacy Agreement above."
                                sx={{ 
                                    mt: 1, 
                                    mb: 0,
                                    '& .MuiFormControlLabel-label': {
                                        color: privacyError ? 'error.main' : 'inherit',
                                    },
                                    border: privacyError ? '1px solid' : 'none',
                                    borderColor: privacyError ? 'error.main' : 'transparent',
                                    borderRadius: 1,
                                    p: privacyError ? 1 : 0,
                                }}
                            />
                        </Box>
                    )}

                    {/* Row 6: Notification Consent Checkbox - Full Width */}
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formData.notificationConsent}
                                onChange={handleInputChange('notificationConsent')}
                                color="primary"
                            />
                        }
                        label={consentMessage}
                        sx={{ mt: 1, mb: 1 }}
                    />

                    {/* Row 7: Activate Giftcard Button - Full Width */}
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={submitting}
                        sx={{ mt: 2, py: 1.5 }}
                    >
                        {submitting ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            'Activate Giftcard'
                        )}
                    </Button>
                </Box>
            </Paper>

            {/* QR Code Scanner Dialog */}
            {loaded && qrCodeSupported && (
                <QRCodeScanner
                    open={qrScannerOpen}
                    onClose={handleCloseQrScanner}
                    onScanSuccess={handleQrScanSuccess}
                />
            )}
        </Box>
    );
};

export default CustomerForm;
